import { useRef, useEffect, useState } from 'react'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  Bars3Icon,
  ArrowLongRightIcon,
  ArrowLongLeftIcon,
} from '@heroicons/react/24/outline'
import { ChallengeService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useDebouncedEventListener, useEventListener, useEventEmitter } from '@modules/EventBusModule'
import { useTar } from '@/modules/i18n'
import DOMPurify from 'dompurify'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@duck/ui'
import { useIsMobile } from '@duck/ui'
import {
  cleanChartContainersFromElement,
  cleanChartContainersFromHTML,
  getContentChartIds,
  saveSelection,
  restoreSelection,
  insertNodeAtSelection,
  initializeChart,
  createTrashButton,
  rebindExistingTrashButtons,
} from './wysiwygUtils'

interface WysiwygEditorProps {
  onChange?: (content: string) => void
  contentGetter?: () => string
  content?: string
  placeholder?: string
  className?: string
  readOnly?: boolean
  /** When true, this editor reacts to solution:insertHtml and chart:added (e.g. Solution tab only). */
  acceptSolutionInsert?: boolean
  handleChartAdded?: (content: string, chart: ChallengeService.Chart) => void
  handleChartRemoved?: (content: string, remainingChartIds: string[]) => void
  startingCharts?: ChallengeService.WorkState['charts']
  editorClassName?: string
}

export const WysiwygEditor = ({
  onChange,
  contentGetter,
  content = '',
  placeholder = 'Start typing...',
  className = '',
  readOnly = false,
  acceptSolutionInsert = false,
  startingCharts = {},
  handleChartAdded,
  handleChartRemoved,
  editorClassName,
}: WysiwygEditorProps) => {
  const { t } = useTar()
  const isMobile = useIsMobile()
  const editorRef = useRef<HTMLDivElement>(null)
  const loadedChartIds = useRef<string[]>(Object.keys(startingCharts))
  const [formatBlock, setFormatBlock] = useState<string>('p')
  const [textFormatGroupValue, setTextFormatGroupValue] = useState<string>('')
  const [listGroupValue, setListGroupValue] = useState<string>('')
  const [indentGroupValue, setIndentGroupValue] = useState<string>('')
  const savedSelectionRef = useRef<Range | null>(null)
  const eventListenerCleanupRef = useRef<(() => void) | void>(undefined)
  const lastSerializedContentRef = useRef<string | null>(null)
  const skipNextContentSyncRef = useRef<boolean>(false)
  const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw')
  const editorIdRef = useRef<string>('')
  if (!editorIdRef.current) {
    editorIdRef.current = `wysiwyg-${Math.random().toString(36).slice(2, 10)}`
  }

  // Save selection on user interaction
  useEffect(() => {
    if (readOnly || !editorRef.current) {
      return
    }

    const saveSelectionHandler = () => {
      savedSelectionRef.current = saveSelection(editorRef)
    }

    const editor = editorRef.current
    editor.addEventListener('click', saveSelectionHandler)
    editor.addEventListener('keyup', saveSelectionHandler)
    editor.addEventListener('mouseup', saveSelectionHandler)

    return () => {
      editor.removeEventListener('click', saveSelectionHandler)
      editor.removeEventListener('keyup', saveSelectionHandler)
      editor.removeEventListener('mouseup', saveSelectionHandler)
    }
  }, [readOnly])

  // Detect if text contains any Hebrew characters
  const containsHebrew = (text: string): boolean => {
    if (text.length === 0) return false
    // Check if any character is Hebrew (Unicode range \u0590-\u05FF)
    return /[\u0590-\u05FF]/.test(text)
  }

  // Get text content from an element, ignoring nested formatting
  const getTextContent = (element: Node): string => {
    if (element.nodeType === Node.TEXT_NODE) {
      return element.textContent || ''
    }
    if (element.nodeType === Node.ELEMENT_NODE) {
      const el = element as HTMLElement
      // Skip chart containers and other non-text elements
      if (el.classList.contains('chart-container')) {
        return ''
      }
      let text = ''
      for (const child of Array.from(el.childNodes)) {
        text += getTextContent(child)
      }
      return text
    }
    return ''
  }

  // Wrap bare text nodes that are direct children of the editor root
  // in a block-level element so they are processed by RTL/LTR logic.
  const wrapBareTextNodesInRoot = () => {
    const root = editorRef.current
    if (!root) return

    const childNodes = Array.from(root.childNodes)
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? ''
        // Ignore pure whitespace text nodes to avoid creating empty blocks
        if (text.trim().length === 0) {
          return
        }

        const wrapper = document.createElement('p')
        wrapper.textContent = text
        root.replaceChild(wrapper, node)
      }
    })
  }

  // Apply RTL/LTR direction to block elements based on Hebrew detection
  const applyRtlToBlocks = () => {
    if (!editorRef.current) return

    // Ensure bare text nodes at the root are wrapped so they can be styled
    wrapBareTextNodesInRoot()

    const blockElements = editorRef.current.querySelectorAll(
      'p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre'
    )

    blockElements.forEach((element) => {
      const el = element as HTMLElement
      // Skip chart containers
      if (el.classList.contains('chart-container')) {
        return
      }

      const textContent = getTextContent(el)
      const isRtl = containsHebrew(textContent)
      if (isRtl) {
        el.setAttribute('dir', 'rtl')
      } else {
        el.setAttribute('dir', 'ltr')
      }
    })
  }

  const externalContent = contentGetter?.() ?? content ?? ''

  // Content synchronization effect
  useEffect(() => {
    if (!editorRef.current) {
      return
    }

    const currentContent = editorRef.current.innerHTML
    const newContent = externalContent
    const normalizedCurrentContent = cleanChartContainersFromHTML(currentContent)

    // If this content value is just the echo of our own latest
    // `updateContent` call, avoid resetting innerHTML – this would
    // clear chart internals and reset the caret for no visual change.
    if (
      skipNextContentSyncRef.current &&
      lastSerializedContentRef.current !== null &&
      lastSerializedContentRef.current === newContent
    ) {
      skipNextContentSyncRef.current = false
      return
    }

    // Only update if content actually changed in a meaningful way.
    // If the only difference is chart internals (which we deliberately strip
    // from serialized HTML), skip resetting innerHTML to preserve charts and caret.
    if (currentContent === newContent || normalizedCurrentContent === newContent) {
      return
    }

    const root = editorRef.current
    const sanitized = DOMPurify.sanitize(newContent)

    // Build a new DOM tree for the updated content, but preserve any existing
    // chart container internals by copying them over from the current DOM.
    const temp = document.createElement('div')
    temp.innerHTML = sanitized

    const existingCharts = root.querySelectorAll<HTMLElement>('.chart-container[data-chart-id]')
    existingCharts.forEach((existingChart) => {
      const id = existingChart.getAttribute('data-chart-id')
      if (!id) return
      const tempChart = temp.querySelector<HTMLElement>(`.chart-container[data-chart-id="${id}"]`)
      if (tempChart) {
        tempChart.innerHTML = existingChart.innerHTML
      }
    })

    root.innerHTML = temp.innerHTML

    // After external content updates, re-apply RTL logic and rebind trash buttons.
    setTimeout(() => {
      applyRtlToBlocks()
      rebindExistingTrashButtons(updateContent)
    }, 0)
  }, [externalContent])

  const rerenderCharts = (): (() => void) | void => {
    if (!editorRef.current || Object.keys(startingCharts).length === 0) {
      return
    }
    const cleanupFunctions: (() => void)[] = []
    cleanChartContainersFromElement(editorRef.current)
    if (Object.keys(startingCharts).length > 0) {
      Object.entries(startingCharts).forEach(([id, chart]) => {
        const cleanup = initializeChart(id, chart, editorRef, readOnly, updateContent)
        if (cleanup) {
          cleanupFunctions.push(cleanup)
        }
      })
    }
    // Apply RTL detection after charts are rendered
    setTimeout(() => {
      applyRtlToBlocks()
    }, 0)
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup?.())
    }
  }

  // Chart initialization on mount
  useEffect(() => {
    return rerenderCharts()
  }, [startingCharts])

  // Cleanup event listener cleanups on unmount or when dependencies change
  useEffect(() => {
    return () => {
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
        eventListenerCleanupRef.current = undefined
      }
    }
  }, [startingCharts, readOnly, handleChartAdded])

  useDebouncedEventListener(60, 'charts:triggerRedraw', (event) => {
    // If this redraw was triggered by this exact editor instance,
    // skip it so we don't disrupt the active typing experience.
    if (event.originId && event.originId === editorIdRef.current) {
      return
    }
    // Cleanup previous cleanup if it exists
    if (eventListenerCleanupRef.current) {
      eventListenerCleanupRef.current()
    }
    // Store new cleanup function
    eventListenerCleanupRef.current = rerenderCharts()
  }, [startingCharts])

  useEventListener('chart:added', ({ chart }) => {
    if (!acceptSolutionInsert || !chart.id || !editorRef.current) {
      return
    }

    // Restore the saved selection if available
    if (savedSelectionRef.current) {
      restoreSelection(savedSelectionRef.current)
    }

    const chartContainer = document.createElement('div')
    chartContainer.className = `chart-container w-full relative my-2 p-2 border border-border rounded-md overflow-hidden`
    chartContainer.setAttribute('data-chart-id', chart.id)
    const chartContainerId = chartContainer.id || `chart-${chart.id}`
    chartContainer.id = chartContainerId

    // Insert at the current selection position (or saved position)
    insertNodeAtSelection(chartContainer, editorRef)

    const separator = document.createElement('div')
    separator.innerHTML = '&nbsp;'
    insertNodeAtSelection(separator, editorRef)

    if (handleChartAdded) { // will trigger a rerender and hit of the rerenderCharts useEffect
      handleChartAdded(cleanChartContainersFromHTML(editorRef.current?.innerHTML || ''), chart)
      loadedChartIds.current.push(chart.id)
    } else {
      // Cleanup previous cleanup if it exists
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
      }
      // Store new cleanup function
      eventListenerCleanupRef.current = rerenderCharts()
    }
  }, [acceptSolutionInsert, readOnly, handleChartAdded])

  useEventListener('solution:insertHtml', ({ value: html, deleteButton }) => {
    if (!acceptSolutionInsert || !editorRef.current || readOnly) return
    if (savedSelectionRef.current) {
      restoreSelection(savedSelectionRef.current)
    }
    const temp = document.createElement('div')
    temp.innerHTML = DOMPurify.sanitize(html)
    const baseNode = temp.childNodes.length === 1 && temp.firstChild ? temp.firstChild : temp

    const nodeToInsert =
      deleteButton && !readOnly
        ? (() => {
          const container = document.createElement('div')
          container.className = 'relative my-2'
          const blockId = `solution-block-${Math.random().toString(36).slice(2, 10)}`
          container.id = blockId
          container.appendChild(baseNode)
          const trashButton = createTrashButton(blockId, updateContent)
          container.appendChild(trashButton)
          return container
        })()
        : baseNode

    insertNodeAtSelection(nodeToInsert, editorRef)
    updateContent()
  }, [acceptSolutionInsert, readOnly])

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    updateContent()
    // For Solution editors only: emit chart redraw so other Solution editors
    // re-initialize after formatting (bold/italic/lists). Notes must not emit.
    if (acceptSolutionInsert) {
      emitChartsTriggerRedraw({ originId: editorIdRef.current })
    }
  }

  const updateContent = () => {
    if (editorRef.current) {
      const remainingChartIds = getContentChartIds(editorRef.current)
      if (remainingChartIds.length < loadedChartIds.current.length) {
        if (handleChartRemoved) {
          handleChartRemoved(cleanChartContainersFromHTML(editorRef.current.innerHTML), remainingChartIds)
        }
        loadedChartIds.current = remainingChartIds
        return
      }
      const serialized = cleanChartContainersFromHTML(editorRef.current.innerHTML)
      lastSerializedContentRef.current = serialized
      skipNextContentSyncRef.current = true
      onChange?.(serialized)
      // When content changes in a Solution editor (one with charts), notify
      // other Solution editors to redraw their charts. Do not emit from Notes
      // or other non-solution editors so they don't trigger Solution chart redraws.
      if (acceptSolutionInsert) {
        emitChartsTriggerRedraw({ originId: editorIdRef.current })
      }
    }
  }

  const handleInput = () => {
    applyRtlToBlocks()
    updateContent()
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  const increaseIndent = () => {
    execCommand('indent')
  }

  const decreaseIndent = () => {
    execCommand('outdent')
  }

  return (
    <div
      className={cn('p-2 w-full flex flex-col flex-1 min-h-0 overflow-hidden', className)}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div className="bg-card flex flex-col gap-1 p-2 w-full border-border border-b">
          <span className="text-xs font-medium text-muted-foreground px-1">
            {t('common.formatting')}
          </span>
          <div className="flex items-center gap-1 h-7">
            {/* Headers */}
            <Select
              value={formatBlock}
              onValueChange={(value) => {
                setFormatBlock(value)
                if (value === 'p') {
                  execCommand('formatBlock', '<p>')
                } else {
                  execCommand('formatBlock', `<${value}>`)
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-7 bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="p">{t('common.paragraph')}</SelectItem>
                <SelectItem value="h1">{t('common.heading1')}</SelectItem>
                <SelectItem value="h2">{t('common.heading2')}</SelectItem>
                <SelectItem value="h3">{t('common.heading3')}</SelectItem>
                <SelectItem value="h4">{t('common.heading4')}</SelectItem>
              </SelectContent>
            </Select>

            <div className="w-px h-6 bg-border mx-1" />

            {!isMobile && (
              <>
                {/* Text formatting group */}
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  value={textFormatGroupValue}
                  onValueChange={(value) => {
                    if (!value) return
                    setTextFormatGroupValue('')
                    const handlers: Record<string, () => void> = {
                      bold: () => execCommand('bold'),
                      italic: () => execCommand('italic'),
                      underline: () => execCommand('underline'),
                    }
                    handlers[value]?.()
                  }}
                  aria-label={t('common.textFormatting') ?? 'Text formatting'}
                >
                  <ToggleGroupItem
                    value="bold"
                    aria-label={t('common.bold')}
                    className="gap-1.5"
                  >
                    <BoldIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="italic"
                    aria-label={t('common.italic')}
                    className="gap-1.5"
                  >
                    <ItalicIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="underline"
                    aria-label={t('common.underline')}
                    className="gap-1.5"
                  >
                    <UnderlineIcon className="w-4 h-4" />
                  </ToggleGroupItem>
                </ToggleGroup>

                <div className="w-px h-6 bg-border mx-1" />
              </>
            )}

            {/* Lists group */}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={listGroupValue}
              onValueChange={(value) => {
                if (!value) return
                setListGroupValue('')
                const handlers: Record<string, () => void> = {
                  unordered: () => execCommand('insertUnorderedList'),
                  ordered: () => execCommand('insertOrderedList'),
                }
                handlers[value]?.()
              }}
              aria-label={t('common.lists') ?? 'Lists'}
            >
              <ToggleGroupItem
                value="unordered"
                aria-label={t('common.unorderedList')}
                className="gap-1.5"
              >
                <ListBulletIcon className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="ordered"
                aria-label={t('common.orderedList')}
                className="gap-1.5"
              >
                <Bars3Icon className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="w-px h-6 bg-border mx-1" />

            {/* Indentation group */}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={indentGroupValue}
              onValueChange={(value) => {
                if (!value) return
                setIndentGroupValue('')
                const handlers: Record<string, () => void> = {
                  decrease: () => decreaseIndent(),
                  increase: () => increaseIndent(),
                }
                handlers[value]?.()
              }}
              aria-label={t('common.indentation') ?? 'Indentation'}
            >
              <ToggleGroupItem
                value="decrease"
                aria-label={t('common.decreaseIndent')}
                className="gap-1.5"
              >
                <ArrowLongLeftIcon className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem
                value="increase"
                aria-label={t('common.increaseIndent')}
                className="gap-1.5"
              >
                <ArrowLongRightIcon className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      )}

      {/* Editor Container - scroll lives here so toolbar stays fixed */}
      <div className={cn("overflow-auto flex flex-col flex-1 min-h-0 w-full custom-scrollbar", editorClassName)}>
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={readOnly ? undefined : handleInput}
          onPaste={readOnly ? undefined : handlePaste}
          className={cn(
            'wysiwyg-editor p-4 w-full max-w-[800px] min-h-full text-foreground focus:outline-none [&_span]:!text-foreground',
            readOnly && 'wysiwyg-read-only' // this class is used to hide the trash buttons
          )}
          style={{
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
          data-placeholder={placeholder}
        />
      </div>
    </div>
  )
} 